import Link from 'next/link'
import Head from 'next/head'
import Seo, { SITE_URL } from '@comp/seo'
import JsonLd from '@comp/structured-data'
import Layout from '@comp/layout'
import Container from '@comp/ui/container'
import Section from '@comp/ui/section'
import Eyebrow from '@comp/ui/eyebrow'
import Heading from '@comp/ui/heading'
import Text from '@comp/ui/text'
import Button from '@comp/ui/button'
import { format } from 'date-fns'
import { tr as trLocale } from 'date-fns/locale'
import {
  glossaryTerms,
  getGlossaryTerm,
  pick,
  GlossaryTerm,
  GLOSSARY_LAST_UPDATED,
} from '@lib/glossary'

const products = {
  sign: {
    name: 'Monosign',
    href: '/sign',
    eyebrow: { en: 'Identity & Access Management', tr: 'Kimlik ve Erişim Yönetimi' },
    pitch: {
      en: 'SSO, MFA, and lifecycle automation for 7,000+ apps, self-hosted or cloud.',
      tr: "7.000'den fazla uygulama için SSO, MFA ve yaşam döngüsü otomasyonu, kendi altyapınızda veya bulutta.",
    },
  },
  pam: {
    name: 'Monopam',
    href: '/pam',
    eyebrow: { en: 'Privileged Access Management', tr: 'Ayrıcalıklı Erişim Yönetimi' },
    pitch: {
      en: 'Vault credentials, record privileged sessions, and grant just-in-time access from one console.',
      tr: 'Kimlik bilgilerini kasada saklayın, ayrıcalıklı oturumları kaydedin ve tam zamanında erişimi tek konsoldan yönetin.',
    },
  },
  sync: {
    name: 'Monosync',
    href: '/sync',
    eyebrow: { en: 'Identity Governance', tr: 'Kimlik Yönetişimi' },
    pitch: {
      en: 'Sync every directory, map entitlements, and run audit-ready access reviews.',
      tr: 'Tüm dizinleri senkronize edin, yetkilendirmeleri haritalayın ve denetime hazır erişim gözden geçirmeleri yürütün.',
    },
  },
}

const ui = {
  backToGlossary: { en: '← All terms', tr: '← Tüm terimler' },
  faqHeading: { en: 'Frequently asked questions', tr: 'Sık sorulan sorular' },
  relatedHeading: { en: 'Related terms', tr: 'İlgili terimler' },
  ctaLead: { en: 'Put this into practice with', tr: 'Bunu uygulamaya geçirin:' },
  ctaExplore: { en: 'Explore', tr: 'Keşfet:' },
  ctaTrial: { en: 'Start free trial', tr: 'Ücretsiz deneme başlat' },
}

type Props = {
  term: GlossaryTerm
  related: { slug: string; label: string }[]
  locale: string
}

export default function GlossaryTermPage({ term, related, locale }: Props) {
  const basePath = locale === 'tr' ? '/tr' : ''
  const product = products[term.product]
  return (
    <Layout>
      <Seo
        title={`${pick(term.title, locale)} | Monofor`}
        description={pick(term.short, locale)}
        path={`${basePath}/glossary/${term.slug}`}
      />
      <Head>
        <meta
          property="article:modified_time"
          content={new Date(GLOSSARY_LAST_UPDATED).toISOString()}
        />
        <link
          rel="alternate"
          hrefLang="en"
          href={`${SITE_URL}/glossary/${term.slug}`}
        />
        <link
          rel="alternate"
          hrefLang="tr"
          href={`${SITE_URL}/tr/glossary/${term.slug}`}
        />
        <link
          rel="alternate"
          hrefLang="x-default"
          href={`${SITE_URL}/glossary/${term.slug}`}
        />
      </Head>
      <JsonLd
        id="defined-term"
        data={{
          '@type': 'DefinedTerm',
          name: pick(term.term, locale),
          description: pick(term.short, locale),
          inDefinedTermSet: {
            '@type': 'DefinedTermSet',
            name: 'Monofor Identity Security Glossary',
            url: `${SITE_URL}${basePath}/glossary`,
          },
          url: `${SITE_URL}${basePath}/glossary/${term.slug}`,
        }}
      />
      <JsonLd
        id="term-faq"
        data={{
          '@type': 'FAQPage',
          mainEntity: term.faqs.map((f) => ({
            '@type': 'Question',
            name: pick(f.q, locale),
            acceptedAnswer: { '@type': 'Answer', text: pick(f.a, locale) },
          })),
        }}
      />
      <main className="bg-surface text-ink-primary">
        {/* Hero */}
        <section className="border-b border-line-subtle bg-surface-subtle pt-16 pb-12 lg:pt-24 lg:pb-16">
          <Container size="md">
            <Link
              href="/glossary"
              className="inline-flex items-center gap-2 text-body-sm text-ink-tertiary transition hover:text-ink-primary"
            >
              {pick(ui.backToGlossary, locale)}
            </Link>
            <div className="mt-8">
              <Eyebrow tone="brand">{pick(term.term, locale)}</Eyebrow>
            </div>
            <div className="mt-5">
              <Heading as="h1" size="display-lg">
                {pick(term.title, locale)}
              </Heading>
            </div>
            <div className="mt-6 max-w-2xl">
              <Text size="xl" tone="secondary">
                {pick(term.short, locale)}
              </Text>
            </div>
            <div className="mt-6 text-body-sm text-ink-tertiary">
              {locale === 'tr' ? 'Son güncelleme: ' : 'Last updated: '}
              {format(new Date(GLOSSARY_LAST_UPDATED), 'd MMMM yyyy', {
                locale: locale === 'tr' ? trLocale : undefined,
              })}
            </div>
          </Container>
        </section>

        {/* Body */}
        <article className="bg-surface pt-14 pb-8 lg:pt-20 lg:pb-12">
          <Container size="md">
            {term.sections.map((section) => (
              <div key={section.heading.en} className="mb-12 last:mb-0">
                <h2 className="text-display-sm font-medium text-ink-primary">
                  {pick(section.heading, locale)}
                </h2>
                {pick(section.body, locale)
                  .split('\n\n')
                  .map((paragraph, i) => (
                    <p
                      key={i}
                      className="mt-4 text-body-lg leading-relaxed text-ink-secondary"
                    >
                      {paragraph}
                    </p>
                  ))}
              </div>
            ))}
          </Container>
        </article>

        {/* FAQ */}
        <section className="bg-surface pb-14 lg:pb-20">
          <Container size="md">
            <h2 className="text-display-sm font-medium text-ink-primary">
              {pick(ui.faqHeading, locale)}
            </h2>
            <dl className="mt-6 space-y-4">
              {term.faqs.map((faq) => (
                <div
                  key={faq.q.en}
                  className="rounded-2xl border border-line-subtle bg-surface-subtle p-6"
                >
                  <dt className="text-body-lg font-medium text-ink-primary">
                    {pick(faq.q, locale)}
                  </dt>
                  <dd className="mt-3 text-body-md text-ink-secondary">
                    {pick(faq.a, locale)}
                  </dd>
                </div>
              ))}
            </dl>

            {/* Product CTA */}
            <aside className="mt-12 rounded-3xl border border-line-subtle bg-surface-subtle p-8 lg:p-10">
              <div className="text-eyebrow font-semibold uppercase tracking-[0.12em] text-brand-700">
                {pick(product.eyebrow, locale)}
              </div>
              <h2 className="mt-3 text-display-sm font-medium text-ink-primary">
                {pick(ui.ctaLead, locale)}{' '}
                <Link
                  href={product.href}
                  className="text-brand-700 underline-offset-4 hover:underline"
                >
                  {product.name}
                </Link>
                .
              </h2>
              <p className="mt-3 max-w-2xl text-body-md text-ink-secondary">
                {pick(product.pitch, locale)}
              </p>
              <div className="mt-6 flex flex-wrap items-center gap-3">
                <Button href={product.href} variant="primary" size="md">
                  {pick(ui.ctaExplore, locale)} {product.name}
                </Button>
                <Button href="/try/now" variant="outline" size="md">
                  {pick(ui.ctaTrial, locale)}
                </Button>
              </div>
            </aside>

            {/* Related terms */}
            {related.length > 0 && (
              <div className="mt-12 border-t border-line-subtle pt-8">
                <span className="text-eyebrow font-semibold uppercase tracking-[0.12em] text-ink-tertiary">
                  {pick(ui.relatedHeading, locale)}
                </span>
                <div className="mt-4 flex flex-wrap items-center gap-2">
                  {related.map((r) => (
                    <Link
                      key={r.slug}
                      href={`/glossary/${r.slug}`}
                      className="rounded-pill border border-line-subtle bg-surface px-4 py-2 text-body-sm text-ink-secondary transition hover:border-line hover:text-ink-primary"
                    >
                      {r.label}
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </Container>
        </section>
      </main>
    </Layout>
  )
}

type Params = {
  params: { slug: string }
  locale: string
}

export async function getStaticProps({ params, locale }: Params) {
  const term = getGlossaryTerm(params.slug)
  if (!term) {
    return { notFound: true }
  }
  const resolvedLocale = locale ?? 'en'
  const related = term.relatedTerms
    .map((slug) => getGlossaryTerm(slug))
    .filter((t): t is GlossaryTerm => Boolean(t))
    .map((t) => ({ slug: t.slug, label: pick(t.term, resolvedLocale) }))

  return { props: { term, related, locale: resolvedLocale } }
}

export async function getStaticPaths() {
  const paths = ['en', 'tr'].flatMap((locale) =>
    glossaryTerms.map((t) => ({ params: { slug: t.slug }, locale }))
  )
  return { paths, fallback: false }
}
